import { Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import Chevron from "components/Chevron";
import { TicketCartDetails } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import React, { useState } from "react";
import { formatCurrency } from "utils/appUtils";
import FeeDetails from "./FeeDetails";

const CartSummary: React.FC<{
  charge: TicketCartDetails;
}> = (props) => {
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const selectedSubscription = useAppSelector(
    (state) => state.reservationFlow.selectedSubscription
  );
  const { userSelfRequestCompTicketsEnabled = false } =
    selectedSubscription?.planPackage || {};
  const isComp = Boolean(userSelfRequestCompTicketsEnabled);

  const feeStructures = selectedSubscription?.planPackage.feeStructures ?? [];

  const {
    charge: {
      priceType,
      feeItems,
      totalCredits,
      totalPoints,
      totalPrice,
      totalCreditCardCharges,
    },
  } = props;

  // Index fee structures by id for O(1) lookup when classifying fee items.
  const feeStructureById = new Map(feeStructures.map((fs) => [fs.id, fs]));

  // Fee items whose structure is marked rolledIntoTicketPrice are folded into
  // the displayed ticket price rather than shown as a separate fee line.
  const ticketRollupFeeItems = (feeItems ?? []).filter(
    (fi) => feeStructureById.get(fi.planPackageFeeStructureId)?.rolledIntoTicketPrice === true
  );

  // All other fee items (FEE, TAX) are displayed separately via FeeDetails.
  const regularFeeItems = (feeItems ?? []).filter(
    (fi) => !feeStructureById.get(fi.planPackageFeeStructureId)?.rolledIntoTicketPrice
  );

  // Sum of fees rolled into the ticket price, added on top of base ticket cost.
  const ticketRollupAmount = ticketRollupFeeItems.reduce(
    (acc, fi) => acc + Number(fi.totalPrice),
    0
  );

  // Base ticket cost across all held seats.
  const baseTicketTotal = props.charge.items.reduce(
    (acc, item) => acc + Number(item.totalPrice),
    0
  );

  return (
    <BaseCard title="Cart Summary">
      <BaseCardListItem
        title="Amount Due"
        accessoryRight={
          <Text category="s1">
            {formatCurrency(isComp ? 0 : totalCreditCardCharges)}
          </Text>
        }
      />

      <BaseCardListItem
        title="Price Breakdown"
        showArrow={true}
        accessoryRight={
          <Text category="s1">
            {showPriceBreakdown ? (
              <Chevron direction="up" />
            ) : (
              <Chevron direction="down" />
            )}
          </Text>
        }
        onPress={() => setShowPriceBreakdown(!showPriceBreakdown)}
      />

      {showPriceBreakdown && (
        <>
          {/* Ticket price includes any fees configured to roll into the ticket price. */}
          <BaseCardListItem
            title="Ticket Price:"
            description="The base price of your selected ticket(s)"
            accessoryRight={
              <Text category="s1">
                {isComp
                  ? formatCurrency(0, { priceType })
                  : formatCurrency(baseTicketTotal + ticketRollupAmount, { priceType })}
              </Text>
            }
          />

          {/* Only show FEE/TAX line items that are not rolled into the ticket price. */}
          {regularFeeItems.length > 0 && (
            <FeeDetails feeItems={isComp ? [] : regularFeeItems} />
          )}

          <BaseCardListItem
            title="Total Price:"
            description="Includes all fees and taxes"
            accessoryRight={
              <Text category="s1">
                {formatCurrency(isComp ? 0 : totalPrice)!}
              </Text>
            }
          />

          {/* NOTE: totalCreditCardCharges and totalCredits should always be displayed as
              dollars, regardless of the priceType attribute. */}
          {Number(totalPoints) > 0 && (
            <BaseCardListItem
              title="Funds on Account:"
              description="Available bank balance to cover the total price."
              accessoryRight={
                <Text category="s1" style={{ color: "rgb(26, 142, 14)" }}>
                  {formatCurrency(isComp ? 0 : totalPoints, { priceType })!}
                </Text>
              }
            />
          )}
          {Number(totalCredits) > 0 && (
            <BaseCardListItem
              title="Account Credit:"
              description="Available account credit to cover the total price."
              accessoryRight={
                <Text category="s1" style={{ color: "rgb(26, 142, 14)" }}>
                  {formatCurrency(isComp ? 0 : totalCredits, { priceType })!}
                </Text>
              }
            />
          )}
          <BaseCardListItem
            title="Amount Due"
            accessoryRight={
              <Text category="s1">
                {formatCurrency(isComp ? 0 : totalCreditCardCharges)}
              </Text>
            }
          />
        </>
      )}
    </BaseCard>
  );
};

export default CartSummary;
